/**
 * ⟦formato-fecha⟧ `B37` · `R2-UI6` · **`dd-mm-aaaa hh:mm` EN HORA DE CHILE, PARA
 * TODA LA CONSOLA.**
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ESTO ES UNA **COPIA** DE `conversaciones/formato.ts:70`, Y LA COPIA ES LA
 * DECISIÓN — NO EL ATAJO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `R2-UI7` (la pestaña de Auditoría) y `R2-UI3` (A, envíos) necesitan la MISMA
 * fecha que la tabla de Conversaciones ya formatea. Las tres salidas eran:
 *
 *   ① **mover**: borrar la de `conversaciones/formato.ts` y reescribir sus
 *      imports. Es editar `ui-conversaciones` desde `ui-marco`, que es otro
 *      territorio y otra wave.
 *   ② **importar de `conversaciones/formato.ts`**: acopla la pestaña de Auditoría
 *      y la pantalla de Envíos a la pantalla de Conversaciones por un helper de
 *      diez líneas. El día que alguien reorganice `src/conversaciones/`, se caen
 *      tres pantallas que no tienen nada que ver con conversaciones.
 *   ③ **copiar acá, con dueño anotado.** `r2-ui6.md:52` y `:90`.
 *
 * ⇒ ③. **La duplicación es de UNA función pura sin estado**, y el costo está
 * acotado y escrito: si `F5-CA-90`/`CA-92` cambiaran el formato, hay DOS archivos
 * que tocar. `conversaciones/formato.ts` **no se toca acá**; que re-exporte desde
 * este módulo es **deuda anotada con dueño `ui-conversaciones`**.
 *
 * ⚠️ **Lo que `tsc` NO puede ver:** que las dos copias digan lo mismo. Son dos
 * funciones independientes con la misma firma; una que se desincronice compila.
 * Lo sostiene `pruebas/r2ui6-formato-fecha.test.ts`, que las ejerce a las DOS con
 * las mismas entradas y compara — que es el único instrumento que puede.
 */

/**
 * El locale se fija y no se toma del navegador: la consola es de Elige Educar,
 * en Chile. Tomarlo del navegador haría que la misma fecha se viera distinta
 * según la máquina, que es peor que una decisión explícita.
 */
const LOCALE = "es-CL"

/**
 * ⭐ Hora de Chile, FIJA. `F5-CA-92`. No es la del navegador: es **la hora del
 * producto**, que es la única que sirve para reconciliar un mensaje con lo que
 * reporta Meta. El rastro entero de por qué esto dejó de ser «la del navegador»
 * está en `conversaciones/formato.ts`, que es donde ocurrió.
 */
const ZONA_HORARIA = "America/Santiago"

/**
 * `dd-mm-aaaa hh:mm`, en 24 h y en hora de Chile (`F5-CA-90`, `F5-CA-92`).
 *
 * El formato se COMPONE desde `formatToParts` y no se toma del `format()` del
 * locale, por dos cosas que el criterio fija y el locale no:
 *
 * - `es-CL` separa la fecha de la hora con una COMA (`10-09-2026, 09:15`) y el
 *   Anexo B la quita para todas las pantallas (`10-09-2026 09:15`).
 * - El reloj de 12/24 h y el sufijo `a. m.` / `p. m.` NO son iguales en todos los
 *   motores de ICU para el mismo locale; `hourCycle: "h23"` lo fija.
 */
const FORMATO_FECHA = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONA_HORARIA,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
})

/**
 * La fecha y hora en `dd-mm-aaaa hh:mm` y en hora de Chile.
 *
 * Una fecha inválida se muestra como tal en vez de romper la fila. Llega así
 * sólo si el borde mandó algo que no es una fecha, y esconderlo detrás de un
 * guión lo haría indistinguible de un campo vacío legítimo.
 */
export function formatearFecha(fecha: Date): string {
  if (Number.isNaN(fecha.getTime())) return "fecha inválida"
  const partes = FORMATO_FECHA.formatToParts(fecha)
  /*
   * Se lee cada parte por su nombre y los separadores se escriben acá: el guión
   * de la fecha, el espacio entre fecha y hora y los dos puntos del reloj son del
   * criterio (`F5-CA-90`), no del locale. Así una coma o un `p. m.` de ICU no se
   * cuelan en la celda.
   */
  const valor = (tipo: string): string => partes.find((parte) => parte.type === tipo)?.value ?? ""
  return `${valor("day")}-${valor("month")}-${valor("year")} ${valor("hour")}:${valor("minute")}`
}
